import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { PageFrame } from '../../components';
import { fetchNewsArticleBySlug } from '../../services/api';
import { getArticleById } from '../../data/news';
import './NewsArticle.css';

// Simple markdown renderer
const renderMarkdown = (content) => {
  const lines = content.split('\n');
  const elements = [];
  let i = 0;
  let listItems = [];
  let listType = null;
  let tableRows = [];
  let inTable = false;
  let inCodeBlock = false;
  let codeContent = [];

  const flushList = () => {
    if (listItems.length > 0) {
      if (listType === 'ol') {
        elements.push(<ol key={`list-${elements.length}`} className="md-list md-ol">{listItems}</ol>);
      } else {
        elements.push(<ul key={`list-${elements.length}`} className="md-list md-ul">{listItems}</ul>);
      }
      listItems = [];
      listType = null;
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      const headerRow = tableRows[0];
      const bodyRows = tableRows.slice(2); // Skip header and separator
      elements.push(
        <div key={`table-${elements.length}`} className="md-table-wrapper">
          <table className="md-table">
            <thead>
              <tr>{headerRow.map((cell, i) => <th key={i}>{renderInline(cell)}</th>)}</tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(cell)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    }
  };

  const flushCode = () => {
    if (codeContent.length > 0) {
      elements.push(
        <pre key={`code-${elements.length}`} className="md-code-block">
          <code>{codeContent.join('\n')}</code>
        </pre>
      );
      codeContent = [];
      inCodeBlock = false;
    }
  };

  const renderInline = (text) => {
    if (!text) return text;
    
    // Images
    text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '{{IMG:$2:$1}}');
    
    // Bold
    text = text.replace(/\*\*([^*]+)\*\*/g, '{{BOLD:$1}}');
    
    // Italic
    text = text.replace(/\*([^*]+)\*/g, '{{ITALIC:$1}}');
    
    // Inline code
    text = text.replace(/`([^`]+)`/g, '{{CODE:$1}}');

    // Split and render
    const parts = text.split(/({{[^}]+}})/g);
    return parts.map((part, i) => {
      if (part.startsWith('{{BOLD:')) {
        return <strong key={i}>{part.slice(7, -2)}</strong>;
      } else if (part.startsWith('{{ITALIC:')) {
        return <em key={i}>{part.slice(9, -2)}</em>;
      } else if (part.startsWith('{{CODE:')) {
        return <code key={i} className="md-inline-code">{part.slice(7, -2)}</code>;
      } else if (part.startsWith('{{IMG:')) {
        const match = part.match(/{{IMG:([^:]+):([^}]*)}}/);
        if (match) {
          return <img key={i} src={match[1]} alt={match[2]} className="md-image" />;
        }
      }
      return part;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        flushCode();
      } else {
        flushList();
        flushTable();
        inCodeBlock = true;
      }
      i++;
      continue;
    }

    if (inCodeBlock) {
      codeContent.push(line);
      i++;
      continue;
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|')) {
      flushList();
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      if (!inTable) {
        inTable = true;
      }
      tableRows.push(cells);
      i++;
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Horizontal rule
    if (line.trim() === '---' || line.trim() === '***') {
      flushList();
      elements.push(<hr key={`hr-${i}`} className="md-hr" />);
      i++;
      continue;
    }

    // Headers
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h4 key={`h4-${i}`} className="md-h4">{renderInline(line.slice(4))}</h4>);
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      elements.push(<h3 key={`h3-${i}`} className="md-h3">{renderInline(line.slice(3))}</h3>);
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      elements.push(<h2 key={`h2-${i}`} className="md-h2">{renderInline(line.slice(2))}</h2>);
      i++;
      continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      flushList();
      elements.push(<blockquote key={`bq-${i}`} className="md-blockquote">{renderInline(line.slice(2))}</blockquote>);
      i++;
      continue;
    }

    // Unordered list
    if (line.startsWith('- ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(<li key={`li-${i}`}>{renderInline(line.slice(2))}</li>);
      i++;
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^(\d+)\. (.+)/);
    if (olMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(<li key={`li-${i}`}>{renderInline(olMatch[2])}</li>);
      i++;
      continue;
    }

    // Flush any pending list
    flushList();

    // Image on its own line
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(<img key={`img-${i}`} src={imgMatch[2]} alt={imgMatch[1]} className="md-image" />);
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(<p key={`p-${i}`} className="md-paragraph">{renderInline(line)}</p>);
    i++;
  }

  flushList();
  flushTable();
  flushCode();

  return elements;
};

function NewsArticle() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadArticle = async () => {
      setLoading(true);
      const { data, error } = await fetchNewsArticleBySlug(articleId);

      if (error || !data) {
        console.warn('Failed to fetch article from API, trying static data:', error);
        // Fallback to static data if API fails
        const staticArticle = getArticleById(articleId);
        if (staticArticle) {
          setArticle(staticArticle);
        } else {
          setNotFound(true);
        }
      } else {
        setArticle(data);
      }

      setLoading(false);
    };

    loadArticle();
  }, [articleId]);

  const getTagClass = (tag) => {
    switch (tag) {
      case 'event': return 'news-tag event';
      case 'update': return 'news-tag update';
      case 'announcement': return 'news-tag announcement';
      default: return 'news-tag';
    }
  };

  if (loading) {
    return (
      <PageFrame>
        <Link to="/news" className="back-link">← Back to News</Link>
        <div className="news-loading">Loading article...</div>
      </PageFrame>
    );
  }

  if (notFound || !article) {
    return <Navigate to="/news" replace />;
  }

  return (
    <PageFrame>
      <Link to="/news" className="back-link">← Back to News</Link>

      <article className="news-article-full">
        <div className="news-article-header">
          <span className="news-date">{article.date}</span>
          <span className={getTagClass(article.tag)}>
            {article.tag.charAt(0).toUpperCase() + article.tag.slice(1)}
          </span>
        </div>
        <h1 className="news-article-title-full">{article.title}</h1>

        {article.heroImage && (
          <img src={article.heroImage} alt={article.title} className="news-hero-image" />
        )}

        <div className="news-article-content md-content">
          {renderMarkdown(article.content)}
        </div>
      </article>

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Link to="/news" className="btn btn-gold">Back to All News</Link>
      </div>
    </PageFrame>
  );
}

export default NewsArticle;
